import { useEffect, useState } from "react";
import axios from "axios";
import Loading from "../../../components/utils/Loading.js";
import { getWasteStatistic } from "../../../services/container.js";
import { useTranslation } from "react-i18next";
import InfoTip from "../../../components/InfoTip.js";
import { ReactComponent as HelpIcon } from "../../../static/assets/dashboard-help.svg";
import errorIcon from "../../../static/assets/error.svg";
import deleteStatsIcon from "../../../static/assets/delete-stats.svg";
import packageIcon from "../../../static/assets/package.svg";
import assignmentStatsIcon from "../../../static/assets/assignment-stats.svg";
import trendingUpIcon from "../../../static/assets/moving.svg";
import trendingDownIcon from "../../../static/assets/trending_down.svg";
import {
  GLOBAL_ERROR_RATE_OBJECTIVE,
  buildQualityInsights,
} from "../utils/qualityInsights.js";

const computePercentageChange = (current, previous) => {
  if (!previous || previous === 0) return null;
  return Math.round(((current - previous) / previous) * 100);
};

const TrendChip = ({
  value,
  unit = "%",
  positiveIsGood = true,
  reference,
  previousValue,
}) => {
  const hasValue = Number.isFinite(value);
  const isPositive = hasValue && value > 0;
  const isGood = positiveIsGood ? isPositive : hasValue && value < 0;
  const trendIcon = isGood ? trendingUpIcon : trendingDownIcon;

  const toneClass =
    !hasValue || value === 0
      ? "bg-gray-100 text-gray-500"
      : isGood
        ? "bg-[#E9FBFB] text-[#008685]"
        : "bg-[#FDECEF] text-[#E5193C]";

  return (
    <div
      className={`self-start flex flex-row items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${toneClass}`}
    >
      {hasValue && value !== 0 ? (
        <img src={trendIcon} alt="" width={14} height={8} />
      ) : (
        <i className="fas fa-arrow-right" />
      )}
      <span className="font-semibold">
        {isPositive ? "+" : ""}
        {hasValue ? value : "–"}
        {unit}
      </span>
      {reference ? (
        <span>
          {reference}
          {previousValue != null ? ` (${previousValue})` : ""}
        </span>
      ) : null}
    </div>
  );
};

const KeyValueCard = ({ icon, title, value, infoTip, trend, footer }) => (
  <div className="statistics-card flex flex-col flex-1 min-w-[240px] gap-3">
    <div className="flex flex-row items-center gap-3">
      <img src={icon} alt="" width={52} height={52} />
      <div className="flex flex-col">
        <div className="flex items-center gap-1 text-gray-600">
          <span>{title}</span>
          {infoTip ? <InfoTip icon={HelpIcon}>{infoTip}</InfoTip> : null}
        </div>
        <h3 className="font-bold text-xl text-[#000000]">{value}</h3>
      </div>
    </div>
    {trend}
    {footer}
  </div>
);

const StatisticsKeyValuesComponentQuality = ({
  periodHistoryStart,
  periodHistoryStop,
  periodHistoryStartPrevious,
  periodHistoryStopPrevious,
  enablePreviousPeriod,
  keywordFilter,
  handleValidate,
  setHandleValidate,
}) => {
  const { t } = useTranslation();
  const [insights, setInsights] = useState(null);
  const [previousInsights, setPreviousInsights] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setHandleValidate(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = () => {
    setIsLoading(true);
    const keywordValue = keywordFilter?.value ?? [];

    const previousStatisticsRequest =
      enablePreviousPeriod &&
      periodHistoryStartPrevious &&
      periodHistoryStopPrevious
        ? getWasteStatistic({
            periodStart: periodHistoryStartPrevious,
            periodStop: periodHistoryStopPrevious,
            keywordFilter: JSON.stringify(keywordValue),
          })
        : Promise.resolve({
            status: 200,
            data: { statisticsData: null },
          });

    axios
      .all([
        getWasteStatistic({
          periodStart: periodHistoryStart,
          periodStop: periodHistoryStop,
          keywordFilter: JSON.stringify(keywordValue),
        }),
        previousStatisticsRequest,
      ])
      .then(
        axios.spread((responseStatistics, responsePreviousStatistics) => {
          if (responseStatistics.status === 200) {
            const currentInsights = buildQualityInsights(
              responseStatistics.data?.statisticsData,
            );
            const prevInsights = enablePreviousPeriod
              ? buildQualityInsights(
                  responsePreviousStatistics.data?.statisticsData,
                )
              : null;

            setInsights(currentInsights);
            setPreviousInsights(prevInsights);
            setHandleValidate(false);
            setIsLoading(false);
          }
        }),
      );
  };

  useEffect(() => {
    if (handleValidate) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleValidate]);

  if (isLoading || !insights) {
    return (
      <div>
        <Loading height={"35vh"} />
      </div>
    );
  }

  const errorCountChange = enablePreviousPeriod
    ? computePercentageChange(insights.errorCount, previousInsights?.errorCount)
    : null;

  const errorRateChange =
    enablePreviousPeriod && previousInsights
      ? Number(
          (insights.globalErrorRate - previousInsights.globalErrorRate).toFixed(
            1,
          ),
        )
      : null;

  const trackedObjectsChange = enablePreviousPeriod
    ? computePercentageChange(
        insights.trackedObjectsCount,
        previousInsights?.trackedObjectsCount,
      )
    : null;

  return (
    <div className="flex flex-row flex-wrap gap-6">
      <KeyValueCard
        icon={errorIcon}
        title={t("Detected errors")}
        value={insights.errorCount.toLocaleString()}
        trend={
          errorCountChange != null ? (
            <TrendChip
              value={errorCountChange}
              positiveIsGood={false}
              reference={t("vs previous period")}
              previousValue={previousInsights?.errorCount?.toLocaleString()}
            />
          ) : null
        }
      />

      <KeyValueCard
        icon={deleteStatsIcon}
        title={t("Global error rate")}
        value={`${insights.globalErrorRate.toFixed(1)}%`}
        trend={
          errorRateChange != null ? (
            <TrendChip
              value={errorRateChange}
              unit="%"
              positiveIsGood={false}
              reference={t("quality.objectiveAt", {
                rate: GLOBAL_ERROR_RATE_OBJECTIVE,
              })}
            />
          ) : (
            <TrendChip
              value={Number(insights.globalErrorRate.toFixed(1))}
              unit="%"
              positiveIsGood={false}
              reference={t("quality.objectiveAt", {
                rate: GLOBAL_ERROR_RATE_OBJECTIVE,
              })}
            />
          )
        }
      />

      <KeyValueCard
        icon={packageIcon}
        title={t("quality.identifiedSortingErrorTypes")}
        value={insights.trackedObjectsCount}
        trend={
          trackedObjectsChange != null ? (
            <TrendChip
              value={trackedObjectsChange}
              positiveIsGood={true}
              reference={t("quality.identifiedTypes")}
            />
          ) : null
        }
      />

      <KeyValueCard
        icon={assignmentStatsIcon}
        title={t("Most impacted flux")}
        value={insights.mostImpactedFlux?.label ?? "–"}
        footer={
          insights.mostImpactedFlux ? (
            <span className="self-start text-sm font-medium text-[#D98A00]">
              {t("quality.errorsOnFlux", {
                share: insights.mostImpactedFlux.share.toFixed(1),
              })}
            </span>
          ) : null
        }
      />
    </div>
  );
};

export default StatisticsKeyValuesComponentQuality;
